import { useDarkMode } from "@/hooks/useDarkMode";
import { useLoadGoogleMaps } from "@/hooks/useLoadGoogleMaps";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  hoveredVenueIdAtom,
  mobileListViewAtom,
  mapDetailOriginAtom,
  savedPlaceIdToViewAtom,
  viewModeAtom,
} from "@/state/atoms";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import { darkModeStyles, lightModeStyles } from "common/config";
import { Venue, VenuePinType } from "common/types";
import { useAtom } from "jotai";
import React, { useEffect, useMemo, useRef, useState } from "react";
import GreyPin from "@/assets/pins/grey_pin.svg";
import OrangePin from "@/assets/pins/orange_pin.svg";
import BluePin from "@/assets/pins/blue_pin.svg";
import BlackPin from "@/assets/pins/black_pin.svg";
import GreenPin from "@/assets/pins/green_pin.svg";
import PurplePin from "@/assets/pins/purple_pin.svg";
import GreyPinSelected from "@/assets/pins/grey_pin_selected.svg";
import OrangePinSelected from "@/assets/pins/orange_pin_selected.svg";
import AnalyticsService from "@/services/analytics";
import { cn } from "@/lib/utils";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { getPinIcon } from "common/utils/maps";
import { StandardButton } from "@/components/common/StandardButton";
import VenueCard from "@/components/VenueCard/VenueCard";
import { XIcon } from "@phosphor-icons/react";
import YouAre from "@/assets/pins/youAre.svg";
// import VenueLocation from "@/assets/pins/venue.svg";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { useAuth } from "common/hooks";

function MapViewForNearby(props: {
  venueLocation: { latitude: number; longitude: number };
  filteredBusinesses: Venue[];
  venueId: string;
  hidden?: boolean;
}) {
  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [viewMode] = useAtom(viewModeAtom);
  const { isMapper } = useAuth();
  const isMapperViewActive = isMapper && viewMode === "mapper";

  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [tooltipVenue, setTooltipVenue] = useState<Venue | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  // Snippet (venue preview) shown when a marker is tapped on the results map.
  const [snippetOpen, setSnippetOpen] = useState<boolean>(false);
  const [snippetPosition, setSnippetPosition] = useState<{
    offset: { x: number; y: number };
    className: string;
  } | null>(null);
  const { selectedVenue, setSelectedVenue } = useSelectedVenue();
  const [, setMobileListView] = useAtom(mobileListViewAtom);
  const [, setDetailOrigin] = useAtom(mapDetailOriginAtom);
  const [hoveredVenueId, setHoveredVenueId] = useAtom(hoveredVenueIdAtom);
  const { isMapsLoaded } = useLoadGoogleMaps();
  const isDark = useDarkMode();
  const markerRefs = useRef(new Map());
  const hasInitialFitBoundsRef = useRef(false);

  const {
    location: userLocation,
    // loading: locationLoading,
    // error: locationError,
    // requestUserLocation,
  } = useGeoLocation();

  const handleLoadVenue = (location: Venue) => {
    // Detail panel lives inside the list container, so we flip to it to show
    // the detail — but remember the user came from the map so "Back to all
    // results" can return them here.
    setDetailOrigin("map");
    setMobileListView(true);
    setSelectedVenue(location);
    setActiveMarker(null);
    setTooltipVenue(null);
    setHoveredMarkerId(null);
  };

  //result of search api
  // const { locations, isLoading } = useHomeVenues();
  const [delayedLocations, setDelayedLocations] = useState<Venue[]>([]);

  useEffect(() => {
    if (props.filteredBusinesses?.length) {
      const finalLocations =
        props.filteredBusinesses.map((e: Venue) => {
          if (e.venuePinType) return e;
          return {
            ...e,
            venuePinType: getPinIcon(e),
          };
        }) || [];

      // Small delay to ensure map is ready
      setTimeout(() => {
        setDelayedLocations(finalLocations);
      }, 100);
    } else {
      // Clear markers when filteredBusinesses is empty
      setDelayedLocations([]);
    }
  }, [props.filteredBusinesses]);

  const locations = delayedLocations;

  // console.log("Map loaded:", isMapsLoaded);
  // console.log("Locations from props:", locations);
  // console.log("Map ref:", mapRef);

  // Show the venue preview snippet for a marker (no venue loading yet).
  const handleShowSnippet = (marker: string) => {
    if (marker === activeMarker && snippetOpen) {
      return;
    }

    const venue = locations.find((l) => l.id === marker);
    if (!venue) return;

    const pos = getSnippetPosition(venue);

    setTooltipVenue(null);
    setHoveredMarkerId(marker);
    setHoveredVenueId(marker);
    setSnippetPosition(pos);
    setActiveMarker(marker);
    setSnippetOpen(true);
  };

  // Close the snippet without loading a venue.
  const handleCloseSnippet = () => {
    setActiveMarker(null);
    setSnippetPosition(null);
    setTooltipVenue(null);
    setHoveredMarkerId(null);
    setHoveredVenueId("");
    setSnippetOpen(false);
  };

  // Determine snippet placement relative to the pin so it stays inside the map
  // viewport (ported from the search map's BasicMapView).
  const getSnippetPosition = (location: Venue) => {
    const defaultWidth = 300;
    const defaultHeight = 400;
    const defaultPinHeight = 40;

    if (!mapRef) {
      return {
        offset: {
          x: -(defaultWidth / 2),
          y: -defaultHeight - defaultPinHeight,
        },
        className: "translate-x-[-50%] translate-y-[-105%]",
      };
    }

    try {
      const bounds = mapRef.getBounds();
      if (!bounds) {
        return {
          offset: {
            x: -(defaultWidth / 2),
            y: -defaultHeight - defaultPinHeight,
          },
          className: "translate-x-[-50%] translate-y-[-105%]",
        };
      }

      // Get the map container dimensions
      const mapDiv = mapRef.getDiv();
      const mapHeight = mapDiv.clientHeight;
      const mapWidth = mapDiv.clientWidth;

      // Convert lat/lng to pixel coordinates
      const projection = mapRef.getProjection();
      if (!projection) {
        return {
          offset: {
            x: -(defaultWidth / 2),
            y: -defaultHeight - defaultPinHeight,
          },
          className: "translate-x-[-50%] translate-y-[-105%]",
        };
      }

      const pinLatLng = new google.maps.LatLng(
        location.latitude,
        location.longitude,
      );
      const scale = Math.pow(2, mapRef.getZoom());
      const worldCoordinate = projection.fromLatLngToPoint(pinLatLng);
      const pinPixelY = worldCoordinate.y * scale;
      const pinPixelX = worldCoordinate.x * scale;

      // Get the map's center pixel position
      const centerLatLng = mapRef.getCenter();
      const centerWorldCoordinate = projection.fromLatLngToPoint(centerLatLng);
      const centerPixelY = centerWorldCoordinate.y * scale;
      const centerPixelX = centerWorldCoordinate.x * scale;

      // Calculate the pin's pixel position relative to the map center
      const pinRelativeY = pinPixelY - centerPixelY;
      const pinRelativeX = pinPixelX - centerPixelX;

      // Pin is at center of map, so its absolute position in viewport
      const pinViewportY = mapHeight / 2 + pinRelativeY;
      const pinViewportX = mapWidth / 2 + pinRelativeX;

      // Snippet actual dimensions
      const actualSnippetHeight = 400; // Actual height of the VenueCard snippet
      const actualSnippetWidth = 300; // Actual width of the VenueCard snippet

      // Snippet dimensions for space calculations (with buffer for shadows, borders, and safety)
      const snippetHeight = 440; // Height + extra buffer for shadows and top safety
      const snippetWidth = 320; // Width + extra buffer for shadows
      const pinHeight = 40; // Height of the pin marker
      const marginHorizontal = 50; // Increased safety margin from left/right edges
      const marginTop = 80; // Extra margin for top edge (more critical due to browser chrome)
      const marginBottom = 50; // Standard margin for bottom edge

      // Calculate available space on all sides
      const spaceAbove = pinViewportY - marginTop;
      const spaceBelow = mapHeight - pinViewportY - pinHeight - marginBottom;
      const spaceLeft = pinViewportX - marginHorizontal;
      const spaceRight = mapWidth - pinViewportX - marginHorizontal;

      // Determine if snippet fits in each direction with buffer
      // For top positioning, add extra 30px buffer to be more conservative
      const fitsAbove = spaceAbove >= snippetHeight + 30;
      const fitsBelow = spaceBelow >= snippetHeight;

      // For horizontal positioning, check if there's space for the full snippet width
      // when centered (need half width on each side) - with extra buffer
      const fitsCentered =
        spaceLeft >= snippetWidth / 2 + 10 &&
        spaceRight >= snippetWidth / 2 + 10;

      // Check if full snippet can fit to the right (when aligned left from pin) - with extra buffer
      const fitsFullyRight = spaceRight >= snippetWidth + 10;

      // Check if full snippet can fit to the left (when aligned right from pin) - with extra buffer
      const fitsFullyLeft = spaceLeft >= snippetWidth + 10;

      // Determine vertical position (above or below) - use actual dimensions for offset
      // Add extra offset padding for top positioning to prevent cutoff
      let yOffset = -actualSnippetHeight - pinHeight - 10; // Extra 10px padding when above
      let translateY = "translate-y-[-105%]";

      if (fitsAbove && !fitsBelow) {
        yOffset = -actualSnippetHeight - pinHeight - 10; // Extra padding
        translateY = "translate-y-[-105%]";
      } else if (fitsBelow && !fitsAbove) {
        yOffset = pinHeight + 5; // Small padding below too
        translateY = "translate-y-[5%]";
      } else if (!fitsAbove && !fitsBelow) {
        // Neither fits perfectly, strongly prefer below to avoid top cutoff
        if (spaceBelow > spaceAbove - 50) {
          // Bias towards below by 50px
          yOffset = pinHeight + 5;
          translateY = "translate-y-[5%]";
        } else {
          yOffset = -actualSnippetHeight - pinHeight - 10;
          translateY = "translate-y-[-105%]";
        }
      } else {
        // Both fit, but prefer below if pin is in top 40% of screen to avoid top edge
        const pinInTopPortion = pinViewportY < mapHeight * 0.4;
        if (pinInTopPortion) {
          // Pin is near top, prefer showing below
          yOffset = pinHeight + 5;
          translateY = "translate-y-[5%]";
        } else {
          // Pin has plenty of space above, show above
          yOffset = -actualSnippetHeight - pinHeight - 10;
          translateY = "translate-y-[-105%]";
        }
      }

      // Determine horizontal position (center, left, or right aligned) - use actual dimensions for offset
      let xOffset = -(actualSnippetWidth / 2); // default: centered
      let translateX = "translate-x-[-50%]";

      if (fitsCentered) {
        // Snippet fits centered, keep default
        xOffset = -(actualSnippetWidth / 2);
        translateX = "translate-x-[-50%]";
      } else if (!fitsFullyRight && fitsFullyLeft) {
        // Too close to right edge, align snippet to the LEFT of the pin
        xOffset = -actualSnippetWidth;
        translateX = "translate-x-[-100%]";
      } else if (!fitsFullyLeft && fitsFullyRight) {
        // Too close to left edge, align snippet to the RIGHT of the pin
        xOffset = 0;
        translateX = "translate-x-[0%]";
      } else if (!fitsFullyLeft && !fitsFullyRight) {
        // Doesn't fit on either side fully, choose side with more space
        if (spaceRight > spaceLeft) {
          xOffset = 0;
          translateX = "translate-x-[0%]";
        } else {
          xOffset = -actualSnippetWidth;
          translateX = "translate-x-[-100%]";
        }
      } else {
        // Both sides fit, prefer centered (already set as default)
        xOffset = -(actualSnippetWidth / 2);
        translateX = "translate-x-[-50%]";
      }

      return {
        offset: { x: xOffset, y: yOffset },
        className: `${translateX} ${translateY}`,
      };
    } catch (error) {
      console.error("Error calculating snippet position:", error);
      return {
        offset: {
          x: -(defaultWidth / 2),
          y: -defaultHeight - defaultPinHeight,
        },
        className: "translate-x-[-50%] translate-y-[-105%]",
      };
    }
  };

  const onMapMoved = () => {
    // console.log("No progresive search on Nearby Map View");
    return;
  };

  const onLoad = React.useCallback(
    function callback(map: google.maps.Map) {
      setMapRef(map);
      // Center map on venue location when first loaded
      if (props.venueLocation) {
        map.panTo({
          lat: props.venueLocation.latitude,
          lng: props.venueLocation.longitude,
        });
        map.setZoom(15);
      }
    },
    [props.venueLocation],
  );

  const onUnmount = React.useCallback(function callback() {
    setMapRef(null);
    hasInitialFitBoundsRef.current = false;
  }, []);

  const showPinIcon = (venue: Venue) => {
    // User view: Only show Orange/Grey pins
    if (!isMapperViewActive) {
      if (venue.venuePinType === VenuePinType.Grey) {
        return GreyPin;
      } else {
        return OrangePin;
      }
    }

    // Mapper view: Show all 6 pin types
    switch (venue.venuePinType) {
      case VenuePinType.Green:
        return GreenPin;
      case VenuePinType.Black:
        return BlackPin;
      case VenuePinType.Blue:
        return BluePin;
      case VenuePinType.Purple:
        return PurplePin;
      case VenuePinType.Orange:
        return OrangePin;
      case VenuePinType.Grey:
        return GreyPin;
      default:
        return GreyPin;
    }
  };

  const getSelectedPinIcon = (venue: Venue) => {
    // User view: Only show Orange/Grey selected pins
    if (!isMapperViewActive) {
      if (venue.venuePinType === VenuePinType.Grey) {
        return GreyPinSelected;
      } else {
        return OrangePinSelected;
      }
    }

    // Mapper view: Show all pin types (selected variants only for Grey/Orange)
    switch (venue.venuePinType) {
      case VenuePinType.Grey:
        return GreyPinSelected;
      case VenuePinType.Orange:
        return OrangePinSelected;
      case VenuePinType.Green:
        return GreenPin;
      case VenuePinType.Black:
        return BlackPin;
      case VenuePinType.Blue:
        return BluePin;
      case VenuePinType.Purple:
        return PurplePin;
      default:
        return GreyPinSelected;
    }
  };

  useEffect(() => {
    // Debugging output to ensure locations are received
    // console.log("locations", locations);

    console.log("venueLocation", props.venueLocation);
    console.log("venueLocation latitude", props.venueLocation.latitude);
    console.log("venueLocation longitude", props.venueLocation.longitude);

    // Initialize pin counters
    let orangePin = 0;
    let greyPin = 0;

    // Iterate over the locations and count pins
    locations.map((location) => {
      if (location.reviewsDetailsSubmitted > 0) {
        orangePin++; // Count orange pin
      } else {
        greyPin++; // Count grey pin
      }
    });

    // Tracking the search event in Google Analytics and Mixpanel
    // console.log("searchPageParams:", searchPageParams);
    AnalyticsService.trackGAEventHandler("nearbyVenuesListSearchPerformed");
    AnalyticsService.trackMx("nearbyVenuesListSearchPerformed", {
      SearchTerm: "",
      totalResults: locations.length,
      orangePin: orangePin,
      greyPin: greyPin,
    });
    // console.log("inside homeSearchPerformed");
  }, []); // Added dependencies for useEffect

  useEffect(() => {
    // Only fit bounds once on initial load — skip on subsequent filter/sort changes
    if (hasInitialFitBoundsRef.current) return;

    if (locations.length && mapRef && !activeMarker) {
      const bounds = new google.maps.LatLngBounds();

      // Add all location markers to bounds
      locations.forEach((location) => {
        if (location.latitude && location.longitude) {
          bounds.extend(
            new google.maps.LatLng(location.latitude, location.longitude),
          );
        }
      });

      // Add venue Location if available
      if (props.venueLocation) {
        bounds.extend(
          new google.maps.LatLng(
            props.venueLocation.latitude,
            props.venueLocation.longitude,
          ),
        );
      }

      // Only fit bounds if we have valid coordinates
      if (!bounds.isEmpty()) {
        mapRef.fitBounds(bounds);
        hasInitialFitBoundsRef.current = true;
      }
    }
  }, [locations, props.venueLocation, mapRef, activeMarker]);

  // code to show venue marker highlighted
  useEffect(() => {
    if (selectedVenue && savedPlaceIdToView == "" && mapRef) {
      const newPosition = new google.maps.LatLng({
        lat: selectedVenue.latitude,
        lng: selectedVenue.longitude,
      });
      mapRef.panTo(newPosition);
      setActiveMarker(selectedVenue.id);
    } else {
      setActiveMarker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue, mapRef]);

  // Close the preview snippet when the user drags or zooms the map.
  useEffect(() => {
    if (!mapRef) return;

    const closeSnippet = () => {
      setActiveMarker(null);
      setSnippetPosition(null);
      setTooltipVenue(null);
      setHoveredMarkerId(null);
      setSnippetOpen(false);
    };

    const dragStartListener = mapRef.addListener("dragstart", closeSnippet);
    const zoomChangedListener = mapRef.addListener("zoom_changed", closeSnippet);

    return () => {
      if (dragStartListener) google.maps.event.removeListener(dragStartListener);
      if (zoomChangedListener) {
        google.maps.event.removeListener(zoomChangedListener);
      }
    };
  }, [mapRef]);

  const mapOptions = {
    keyboardShortcuts: false,
    fullscreenControl: false,
    disableDefaultUI: true,
    ...(isDark
      ? { styles: darkModeStyles } // Add dark mode styles if isDark is true
      : { styles: lightModeStyles }), // Add mapId if isDark is false
  };

  // Escape key handler to return focus to results list
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const mapContainer = document.querySelector(
          '[aria-label="Map view of nearby venues"]',
        );
        if (mapContainer && mapContainer.contains(document.activeElement)) {
          // Move focus to results list
          const resultsPanel = document.getElementById("venue-list-panel");
          if (resultsPanel) {
            resultsPanel.setAttribute("tabindex", "-1");
            resultsPanel.focus();
            resultsPanel.addEventListener(
              "blur",
              () => {
                resultsPanel.removeAttribute("tabindex");
              },
              { once: true },
            );
          }
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Stable reference so @react-google-maps/api doesn't re-pan on every render
  const mapCenterValue = useMemo(
    () =>
      props.venueLocation
        ? {
            lat: props.venueLocation.latitude,
            lng: props.venueLocation.longitude,
          }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.venueLocation?.latitude, props.venueLocation?.longitude],
  );

  return (
    <div
      className={cn(
        "flex-1 relative h-full rounded-[12px] overflow-clip",
        props.hidden ? "hidden" : "block",
      )}
      aria-label="Map view of nearby venues"
      role="region"
    >
      <div className="sr-only">
        Use Tab to navigate map controls. Press Escape to exit map and return to
        results list.
      </div>
      {isMapsLoaded && (
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "100%",
          }}
          center={mapCenterValue}
          zoom={15}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={mapOptions}
          onClick={() => {
            setActiveMarker(null);
            setSnippetPosition(null);
            setTooltipVenue(null);
            setHoveredMarkerId(null);
            setHoveredVenueId("");
            setSnippetOpen(false);
          }}
          onIdle={() => onMapMoved()}
          aria-label="Interactive map showing nearby venues"
        >
          {locations.map((location, index) => (
            <Marker
              ref={(marker) => {
                // console.log("Marker data:", {
                // id: location.id,
                // lat: location.latitude,
                // lng: location.longitude,
                // name: location.name,
                // selected: selectedVenue?.id === location.id,
                // index
                // });
                if (marker) {
                  markerRefs.current.set(location.id, marker);
                }
              }}
              key={location.id}
              position={{
                lat: location.latitude,
                lng: location.longitude,
              }}
              // title=""
              zIndex={
                selectedVenue && selectedVenue.id === location.id ? 1000 : index
              }
              icon={{
                url:
                  selectedVenue && selectedVenue.id === location.id
                    ? getSelectedPinIcon(location)
                    : showPinIcon(location),
                anchor: new google.maps.Point(15, 30),
                size:
                  selectedVenue && selectedVenue.id === location.id
                    ? new google.maps.Size(40, 40)
                    : hoveredMarkerId === location.id ||
                        hoveredVenueId === location.id ||
                        activeMarker === location.id
                      ? new google.maps.Size(42, 42)
                      : new google.maps.Size(30, 30),
                scaledSize:
                  selectedVenue && selectedVenue.id === location.id
                    ? new google.maps.Size(40, 40)
                    : hoveredMarkerId === location.id ||
                        hoveredVenueId === location.id ||
                        activeMarker === location.id
                      ? new google.maps.Size(42, 42)
                      : new google.maps.Size(30, 30),
              }}
              onMouseOver={() => {
                // Ignore hover while a snippet is open
                if (snippetOpen) return;
                setTooltipVenue(location);
                setHoveredMarkerId(location.id);
              }}
              onMouseOut={() => {
                if (snippetOpen) return;
                if (activeMarker !== location.id) {
                  setTooltipVenue(null);
                  setHoveredMarkerId(null);
                }
              }}
              onClick={() => {
                AnalyticsService.trackGAEventHandler(
                  "nearbyVenuesListPickedVenueFromSearchResultsMap",
                );
                AnalyticsService.trackMx(
                  "nearbyVenuesListPickedVenueFromSearchResultsMap",
                  {
                    VenueId: location.id,
                    VenueBrandId: location.brandId,
                    VenueBrandName: location.brandName,
                    VenueAddress: location.address,
                    VenueName: location.name,
                    VenueType: location.businessType,
                  },
                );
                if (selectedVenue) {
                  // A venue detail is already open (desktop side map) — open the
                  // clicked venue directly, no snippet (search case B).
                  handleLoadVenue(location);
                } else {
                  // Results map — show the preview snippet first (search cases A & C).
                  handleShowSnippet(location.id);
                }
              }}
              options={{
                label: {
                  className: "sr-only",
                  text: `${location.name}, ${location.businessType}, ${location.distance} miles away`,
                },
              }}
            >
              {/* Desktop: preview snippet anchored near the pin (md and up) */}
              {!selectedVenue &&
              activeMarker === location.id &&
              markerRefs.current?.get(activeMarker) &&
              snippetPosition ? (
                <OverlayView
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  position={{
                    lat: location.latitude,
                    lng: location.longitude,
                  }}
                  getPixelPositionOffset={() => snippetPosition.offset}
                >
                  {/* Wrapper with pointer-events-none to not block the map */}
                  <div
                    className={`hidden md:block w-[300px] ${snippetPosition.className}`}
                    style={{ pointerEvents: "none" }}
                  >
                    <div
                      className="map-snippet-wrapper relative bg-bg-brand-secondary-default rounded-lg shadow-md w-full"
                      style={{ pointerEvents: "auto" }}
                    >
                      <style>{`
                        .map-snippet-wrapper button.bg-opacity-30 {
                          right: 52px !important;
                        }`}</style>

                      {/* Close button - positioned at right edge */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseSnippet();
                        }}
                        className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                        aria-label="Close venue preview"
                        type="button"
                      >
                        <XIcon
                          size={16}
                          weight="bold"
                          className="text-fg-neutral-strong"
                        />
                      </button>

                      <button
                        type="button"
                        className="w-full text-left rounded p-0 focus-blue pointer-events-auto cursor-pointer"
                        aria-label={`View details for ${location.name}`}
                      >
                        <VenueCard
                          location={location}
                          onOpen={() => handleLoadVenue(location)}
                          savedPlaces={[]}
                          truncateText={true}
                          index={index}
                          totalCount={locations.length}
                          showMetaInfo={true}
                          showOpeningHours={true}
                          showVariantIndicators={true}
                          showDescription={true}
                          showAddress={false}
                          inMapView={true}
                        />
                      </button>
                    </div>
                  </div>
                </OverlayView>
              ) : null}
            </Marker>
          ))}

          {userLocation && (
            <Marker
              key={"userLocation"}
              position={{
                lat: userLocation.latitude,
                lng: userLocation.longitude,
              }}
              title=""
              zIndex={10000}
              icon={{
                url: YouAre,
                anchor: new google.maps.Point(15, 30),
                size: new google.maps.Size(30, 30),
                scaledSize: new google.maps.Size(30, 30),
              }}
            ></Marker>
          )}

          {/* <Marker
            key={"venueLocation"}
            position={{
              lat: props.venueLocation.latitude,
              lng: props.venueLocation.longitude,
            }}
            title=""
            zIndex={5000}
            icon={{
              url: VenueLocation,
              anchor: new google.maps.Point(15, 30),
              size: new google.maps.Size(30, 30),
              scaledSize: new google.maps.Size(30, 30),
            }}
          ></Marker> */}

          {/* Hover tooltip - only when no snippet is open */}
          {(() => {
            if (snippetOpen) return null;
            const displayVenue =
              tooltipVenue ||
              (hoveredVenueId
                ? locations.find((l) => l.id === hoveredVenueId)
                : null);
            return displayVenue ? (
              <OverlayView
                position={{
                  lat: displayVenue.latitude,
                  lng: displayVenue.longitude,
                }}
                mapPaneName={OverlayView.FLOAT_PANE}
                getPixelPositionOffset={(width, height) => ({
                  x: -(width / 2),
                  y: -height - 50,
                })}
              >
                <div className="bg-bg-brand-secondary-default text-fg-neutral-strong px-4 py-2 rounded-lg shadow-lg pointer-events-none w-max max-w-[400px]">
                  <div className="font-medium label-m-medium whitespace-nowrap">
                    {displayVenue.name}
                  </div>
                </div>
              </OverlayView>
            ) : null;
          })()}
        </GoogleMap>
      )}

      {/* Goto current location, +, - */}
      <div
        className="absolute right-0 bottom-0 top-0 flex flex-col items-end justify-end"
        aria-label="Map controls"
      >
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            AnalyticsService.trackGAEventHandler(
              "nearbyVenuesListZoomedInMapView",
            );
            AnalyticsService.trackMx("nearbyVenuesListZoomedInMapView", {});
            mapRef.getZoom() < 17 && mapRef.setZoom(mapRef.getZoom() + 1);
          }}
          aria-label="Zoom in"
          className="p-2 mb-1 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold"
        >
          <FaPlus
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            AnalyticsService.trackGAEventHandler(
              "nearbyVenuesListZoomedOutMapView",
            );
            AnalyticsService.trackMx("nearbyVenuesListZoomedOutMapView", {});
            mapRef.getZoom() > 0 && mapRef.setZoom(mapRef.getZoom() - 1);
          }}
          aria-label="Zoom out"
          className="p-2 mb-8 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold"
        >
          <FaMinus
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
      </div>

      {/* Mobile: fixed bottom snippet (below md) */}
      {!selectedVenue &&
        activeMarker &&
        (() => {
          const location = locations.find((l) => l.id === activeMarker);
          const index = locations.findIndex((l) => l.id === activeMarker);
          if (!location) return null;

          return (
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-[1000] p-3 pb-4 animate-slide-up pointer-events-none">
              <style>{`
                @keyframes slide-up {
                  from { transform: translateY(100%); }
                  to { transform: translateY(0); }
                }
                .animate-slide-up { animation: slide-up 0.3s ease-out; }
                .mobile-snippet-wrapper button.bg-opacity-30 { right: 52px !important; }
                .mobile-snippet-container { max-height: 280px; overflow: hidden; }
                .mobile-snippet-wrapper figure {
                  aspect-ratio: 16 / 7 !important;
                  max-height: 140px !important;
                }
                .mobile-snippet-wrapper figure img { object-fit: cover !important; }`}</style>

              <div className="mobile-snippet-container relative">
                {/* Close button - positioned at right edge */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseSnippet();
                  }}
                  className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                  aria-label="Close venue preview"
                  type="button"
                >
                  <XIcon
                    size={16}
                    weight="bold"
                    className="text-fg-neutral-strong"
                  />
                </button>

                <button
                  type="button"
                  className="w-full text-left rounded p-0 focus-blue mobile-snippet-wrapper cursor-pointer pointer-events-auto"
                  aria-label={`View details for ${location.name}`}
                >
                  <VenueCard
                    location={location}
                    onOpen={() => handleLoadVenue(location)}
                    savedPlaces={[]}
                    truncateText={true}
                    index={index}
                    totalCount={locations.length}
                    showMetaInfo={true}
                    showOpeningHours={true}
                    showVariantIndicators={true}
                    showDescription={true}
                    showAddress={false}
                    inMapView={true}
                  />
                </button>
              </div>
            </div>
          );
        })()}
    </div>
  );
}

export default MapViewForNearby;
